"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Digit from "@/components/common/digit";
import ScrollingText from "@/components/common/scrolling-text";
import { useTranslations } from "@/app/providers";

const Hero = () => {
  const t = useTranslations();
  const [showText, setShowText] = useState(false);
  const [displayedIdea, setDisplayedIdea] = useState("");
  const [scrollX, setScrollX] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // After 1.5 seconds of blinking cursor, start typing "la tua"
    const typingTimer = setTimeout(() => {
      setShowText(true);
      const ideaText = t.hero.your_idea || "la tua";
      let currentIndex = 0;

      const typingInterval = setInterval(() => {
        if (currentIndex < ideaText.length) {
          setDisplayedIdea(ideaText.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          clearInterval(typingInterval);
        }
      }, 80); // Typing speed (80ms per character for natural feel)

      return () => clearInterval(typingInterval);
    }, 1500);

    return () => clearTimeout(typingTimer);
  }, [t.hero.your_idea]);

  // Scroll-linked horizontal movement for title (fast, like Consensys.io)
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      // Fast movement: 1px scroll = 1.5px horizontal movement
      setScrollX(scrollY * 1.5);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section ref={sectionRef} className='relative w-full overflow-hidden'>
      {/* Background with gradient */}
      <div className='absolute inset-0 hero-gradient' />

      {/* Retro grid pattern */}
      <div className='absolute bottom-0 right-0 left-0 top-1/3 z-20'>
        <Image
          src='/retro_grid.svg'
          alt='Retro grid pattern'
          fill
          className='object-cover'
          priority
        />
      </div>

      {/* Hero content */}
      <div className='relative z-10 container mx-auto px-6 pt-28'>
        <div className='flex flex-col gap-18 md:gap-12 flex-1 pt-20'>
          {/* Main title */}
          <div className='max-w-4/5'>
            <h1 className='text-white font-semibold text-[5.5rem] leading-[80%] md:text-[11.25rem]'>
              {/* Desktop: Born From on same line, scrolls together */}
              <span
                className='hidden md:block transition-transform duration-100 ease-out'
                style={{ transform: `translateX(${scrollX}px)` }}
              >
                <span>{t.hero.titleBorn}</span>
                <span className='italic'>{t.hero.titleFrom}</span>
              </span>

              {/* Mobile: Born and From on separate lines, both scroll */}
              <span
                className='block md:hidden transition-transform duration-100 ease-out'
                style={{ transform: `translateX(${scrollX}px)` }}
              >
                {t.hero.titleBorn}
              </span>
              <span
                className='block md:hidden italic transition-transform duration-100 ease-out'
                style={{ transform: `translateX(${scrollX * 0.6}px)` }}
              >
                {t.hero.titleFrom}
              </span>

              {/* Code - stays fixed */}
              <span className='block'>
                {t.hero.titleCode}
                <span className='text-brand-violet-500'>.</span>
              </span>
            </h1>
          </div>

          {/* Descriptive text */}
          <div className='text-brand-violet-100 text-body-1 leading-relaxed max-w-lg self-end whitespace-pre-line'>
            <p>{t.hero.description1}</p>
            <p className='pt-8'>
              <span className='mr-1'>{t.hero.description2}</span>
              {showText && <span>{displayedIdea}</span>}
              <Digit
                width={38}
                height={13}
                className='inline-block digit-blink align-middle'
              />
            </p>
          </div>
        </div>
      </div>

      {/* Scrolling text */}
      <ScrollingText className='mt-20' items={t.scrollingText} />
    </section>
  );
};

export default Hero;
